import { DASHBOARD_LANGUAGE_STORAGE_KEY } from "@/features/localization/constants";
import { signOutFrenchTranslations } from "./sign-out-feedback";

export type DashboardLoginLanguage = "en" | "fr";

export const DASHBOARD_LOGIN_LANGUAGE_STORAGE_KEY = DASHBOARD_LANGUAGE_STORAGE_KEY;

type TranslationParams = Record<string, number | string>;

const frenchCopy: Readonly<Record<string, string>> = Object.freeze({
  ...signOutFrenchTranslations,
  "This sign-in link could not be used. Request a new email or use the QR code.": "Ce lien de connexion n’a pas pu être utilisé. Demandez un nouvel e-mail ou utilisez le code QR.",
  "Try again": "Réessayer",
  "This request timed out. Check your connection and try again.": "La demande a expiré. Vérifiez votre connexion et réessayez.",
  "We could not confirm email delivery. Check your inbox for the latest email, or try again.": "L’envoi de l’e-mail n’a pas pu être confirmé. Consultez le dernier e-mail reçu ou réessayez.",
  "Approved. Signing in...": "Autorisation accordée. Connexion…",
  "ArtCrew app login QR code": "Code QR de connexion à l’application ArtCrew",
  "ArtCrew home": "Accueil ArtCrew",
  "Auth is not configured.": "L’authentification n’est pas configurée.",
  "By continuing, you agree to ArtCrew's": "En continuant, vous acceptez les",
  "Checking...": "Vérification…",
  "Continue": "Continuer",
  "Create your studio": "Créer votre atelier",
  "New to ArtCrew?": "Nouveau sur ArtCrew ?",
  "Cookie Preferences": "Préférences relatives aux cookies",
  "Could not check QR login.": "Impossible de vérifier la connexion par code QR.",
  "Could not create QR login.": "Impossible de créer la connexion par code QR.",
  "Could not finish QR login.": "Impossible de finaliser la connexion par code QR.",
  "Could not send link.": "Impossible d’envoyer le lien.",
  "Dashboard auth is not configured.": "L’authentification du tableau de bord n’est pas configurée.",
  "Dashboard auth returned an invalid service origin.": "L’authentification du tableau de bord a renvoyé une origine de service invalide.",
  "Dashboard authentication": "Authentification au tableau de bord",
  "Dashboard QR login returned an invalid session.": "La connexion au tableau de bord par code QR a renvoyé une session invalide.",
  "Dashboard session active.": "Session du tableau de bord active.",
  "Dashboard session inactive.": "Session du tableau de bord inactive.",
  "Email address": "Adresse e-mail",
  "Email sent. Check your inbox.": "E-mail envoyé. Consultez votre boîte de réception.",
  "Enter the email address associated with your ArtCrew account.": "Saisissez l’adresse e-mail associée à votre compte ArtCrew.",
  "Enter your email.": "Saisissez votre adresse e-mail.",
  "Help": "Aide",
  "Log in with QR code": "Se connecter avec un code QR",
  "Magic link sign in": "Connexion par lien sécurisé",
  "Manage your ArtCrew workspace.": "Gérez votre espace de travail ArtCrew.",
  "Privacy Policy": "Politique de confidentialité",
  "privacy policy": "politique de confidentialité",
  "QR code expiry countdown": "Compte à rebours d’expiration du code QR",
  "QR code sign in": "Connexion par code QR",
  "QR rendering is unavailable in this browser.": "L’affichage du code QR n’est pas disponible dans ce navigateur.",
  "Refreshing QR code...": "Actualisation du code QR…",
  "Report Security Issues": "Signaler un problème de sécurité",
  "Retry QR code": "Réessayer avec le code QR",
  "Scan this code with the ArtCrew app, then approve this browser to log in instantly.": "Scannez ce code avec l’application ArtCrew, puis autorisez ce navigateur pour vous connecter immédiatement.",
  "Scan with the ArtCrew app, then approve this browser.": "Scannez le code avec l’application ArtCrew, puis autorisez ce navigateur.",
  "Sending...": "Envoi…",
  "Sign in": "Se connecter",
  "Sign in with email": "Se connecter par e-mail",
  "Sign-in link sent. Check your inbox.": "Lien de connexion envoyé. Consultez votre boîte de réception.",
  "Sign in to the ArtCrew dashboard.": "Connectez-vous au tableau de bord ArtCrew.",
  "Sign out": "Se déconnecter",
  "Signed in": "Connecté",
  "Support": "Assistance",
  "System Status": "État du système",
  "terms": "conditions d’utilisation",
  "Terms of Use": "Conditions d’utilisation",
  "Unexpected dashboard auth error.": "Erreur inattendue lors de l’authentification au tableau de bord.",
  "Welcome back": "Heureux de vous retrouver",
  "Your studio, ready when you are.": "Votre atelier, prêt quand vous l’êtes.",
  "Artworks, documents, website and socials, together in one calm place.": "Œuvres, documents, site web et réseaux sociaux, réunis dans un seul espace serein.",
  "Sign-in link sent. Open the email from ArtCrew and press “Sign in to ArtCrew”.": "Lien de connexion envoyé. Ouvrez l’e-mail d’ArtCrew et appuyez sur « Sign in to ArtCrew ».",
  "Check your email": "Consultez vos e-mails",
  "We sent a sign-in email to": "Nous avons envoyé un e-mail de connexion à",
  "Open your email inbox.": "Ouvrez votre boîte de réception.",
  "Find the email called “Your secure ArtCrew sign-in link”.": "Cherchez l’e-mail intitulé « Your secure ArtCrew sign-in link ».",
  "Press the “Sign in to ArtCrew” button in that email. ArtCrew will open and sign you in.": "Appuyez sur le bouton « Sign in to ArtCrew » dans cet e-mail. ArtCrew s’ouvrira et vous serez connecté.",
  "The email can take a minute to arrive. If you can’t find it, look in your Spam or Junk folder.": "L’e-mail peut mettre une minute à arriver. Si vous ne le trouvez pas, regardez dans le dossier Spam ou Courrier indésirable.",
  "Send the email again": "Renvoyer l’e-mail",
  "Use a different email address": "Utiliser une autre adresse e-mail",
  // Server, browser and QR errors that can reach the status line.
  "Request origin is not allowed.": "La connexion n’est pas autorisée depuis cette page. Ouvrez ArtCrew depuis son adresse habituelle, puis réessayez.",
  "Origin not allowed.": "La connexion n’est pas autorisée depuis cette page. Ouvrez ArtCrew depuis son adresse habituelle, puis réessayez.",
  "Dashboard login is temporarily unavailable.": "La connexion est momentanément indisponible. Réessayez dans quelques minutes.",
  "Secure dashboard sign-in is temporarily unavailable.": "La connexion est momentanément indisponible. Réessayez dans quelques minutes.",
  "The dashboard login request could not be completed.": "La connexion n’a pas pu aboutir. Patientez quelques minutes, puis réessayez.",
  "A signed-in ArtCrew account is required.": "Vous devez être connecté à un compte ArtCrew.",
  "QR login challenge is unavailable.": "Ce code QR n’est plus valable. Appuyez sur le bouton d’actualisation pour en afficher un nouveau.",
  "Unknown QR login action.": "La connexion par code QR n’a pas pu aboutir. Réessayez.",
  "Dashboard QR login returned an invalid challenge.": "Le code QR n’a pas pu être préparé. Réessayez.",
  "Dashboard QR login returned an untrusted payload.": "Le code QR n’a pas pu être préparé. Réessayez.",
  "Dashboard QR login payload contains unexpected data.": "Le code QR n’a pas pu être préparé. Réessayez.",
  "Dashboard QR login payload is malformed.": "Le code QR n’a pas pu être préparé. Réessayez.",
  "Dashboard QR login payload does not match its challenge.": "Le code QR n’a pas pu être préparé. Réessayez.",
  "Dashboard QR login returned an invalid status.": "La connexion par code QR n’a pas pu aboutir. Réessayez.",
  "Dashboard QR login returned an invalid destination.": "La connexion par code QR n’a pas pu aboutir. Réessayez.",
  "Sign-in was interrupted. Please try again.": "La connexion a été interrompue. Veuillez réessayer.",
  "Failed to fetch": "Impossible de joindre ArtCrew. Vérifiez votre connexion Internet, puis réessayez.",
  "Load failed": "Impossible de joindre ArtCrew. Vérifiez votre connexion Internet, puis réessayez.",
  "NetworkError when attempting to fetch resource.": "Impossible de joindre ArtCrew. Vérifiez votre connexion Internet, puis réessayez.",
});

const frenchStatusFallback = "Un problème est survenu. Patientez un instant, puis réessayez ou utilisez le code QR.";
const frenchValues = new Set(Object.values(frenchCopy));

export function readDashboardLoginLanguage(): DashboardLoginLanguage {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DASHBOARD_LOGIN_LANGUAGE_STORAGE_KEY) === "fr"
      ? "fr"
      : "en";
  } catch {
    return "en";
  }
}

export function translateDashboardLoginCopy(
  source: string,
  params?: TranslationParams,
  language: DashboardLoginLanguage = readDashboardLoginLanguage(),
): string {
  const translated = language === "fr" ? frenchCopy[source] ?? frenchPattern(source) ?? source : source;
  return interpolate(translated, params);
}

// Status messages can come from the server, the auth SDK or the browser. In
// French, anything without a translation falls back to a plain French message
// rather than showing raw English.
export function translateDashboardLoginStatus(
  source: string,
  language: DashboardLoginLanguage = readDashboardLoginLanguage(),
): string {
  if (language !== "fr") return source;
  if (frenchValues.has(source)) return source;
  return frenchCopy[source] ?? frenchPattern(source) ?? frenchStatusFallback;
}

export function localizeDashboardLoginDocument(language = readDashboardLoginLanguage()): void {
  if (typeof document === "undefined") {
    return;
  }
  const t = (source: string) => translateDashboardLoginCopy(source, undefined, language);
  document.documentElement.lang = language;
  document.title = language === "fr" ? "Connexion ArtCrew" : "ArtCrew sign in";
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", t("Sign in to the ArtCrew dashboard."));

  const textBySelector: Record<string, string> = {
    ".auth-header h1": "Welcome back",
    ".auth-header p": "Enter the email address associated with your ArtCrew account.",
    ".legal-footer a[href='/help']": "Support",
    ".legal-footer a[href='/privacy']": "Privacy Policy",
    ".legal-footer a[href='/terms']": "Terms of Use",
    ".legal-footer button:nth-of-type(1)": "System Status",
    ".legal-footer button:nth-of-type(2)": "Report Security Issues",
    ".legal-footer button:nth-of-type(3)": "Cookie Preferences",
    ".qr-copy h2": "Log in with QR code",
    ".qr-copy > p": "Scan this code with the ArtCrew app, then approve this browser to log in instantly.",
    ".session-card h2": "Signed in",
    ".stage-copy h2": "Your studio, ready when you are.",
    ".stage-copy p": "Artworks, documents, website and socials, together in one calm place.",
    ".sent-panel h2": "Check your email",
    ".sent-intro": "We sent a sign-in email to",
    ".sent-steps li:nth-child(1) span": "Open your email inbox.",
    ".sent-steps li:nth-child(2) span": "Find the email called “Your secure ArtCrew sign-in link”.",
    ".sent-steps li:nth-child(3) span": "Press the “Sign in to ArtCrew” button in that email. ArtCrew will open and sign you in.",
    ".sent-tip": "The email can take a minute to arrive. If you can’t find it, look in your Spam or Junk folder.",
    "#sent-resend": "Send the email again",
    "#sent-change": "Use a different email address",
    ".top-link": "Help",
    "#magic-submit": "Sign in with email",
    "#session-email": "Dashboard session active.",
    "#sign-out-button": "Sign out",
    ".new-account span": "New to ArtCrew?",
    ".new-account a": "Create your studio",
  };
  Object.entries(textBySelector).forEach(([selector, source]) => {
    const node = document.querySelector<HTMLElement>(selector);
    if (node) node.textContent = t(source);
  });

  const attributeCopy: Array<[string, string, string]> = [
    [".brand", "aria-label", "ArtCrew home"],
    [".login-wrap", "aria-label", "Dashboard authentication"],
    [".auth-section#magic-section", "aria-label", "Magic link sign in"],
    ["#qr-section", "aria-label", "QR code sign in"],
    ["#qr-canvas", "aria-label", "ArtCrew app login QR code"],
    ["#qr-expiry", "aria-label", "QR code expiry countdown"],
    ["#refresh-qr-button", "aria-label", "Retry QR code"],
  ];
  attributeCopy.forEach(([selector, attribute, source]) => document.querySelector(selector)?.setAttribute(attribute, t(source)));
  if (language === "fr") document.querySelector(".new-account a")?.setAttribute("href", "/onboarding/?lang=fr");
  const email = document.querySelector<HTMLInputElement>("#email-input");
  if (email) email.placeholder = t("Email address");
  const emailLabel = document.querySelector<HTMLLabelElement>('label[for="email-input"]');
  if (emailLabel) emailLabel.textContent = t("Email address");

  const finePrint = document.querySelector<HTMLElement>(".fine-print");
  const finePrintLinks = finePrint?.querySelectorAll<HTMLAnchorElement>("a");
  if (finePrint && finePrintLinks?.length === 2) {
    finePrint.replaceChildren(
      document.createTextNode(`${t("By continuing, you agree to ArtCrew's")} `),
      finePrintLinks[0],
      document.createTextNode(language === "fr" ? " et la " : " and "),
      finePrintLinks[1],
      document.createTextNode("."),
    );
    finePrintLinks[0].textContent = t("terms");
    finePrintLinks[1].textContent = t("privacy policy");
  }
}

function frenchPattern(source: string): string | null {
  const approved = /^Approved for (.+)\. Signing in\.\.\.$/.exec(source);
  if (approved) return `Autorisation accordée pour ${approved[1]}. Connexion…`;
  const refresh = /^QR code refreshes automatically in (\d+) seconds?\.$/.exec(source);
  if (refresh) return `Le code QR s’actualise automatiquement dans ${refresh[1]} seconde${refresh[1] === "1" ? "" : "s"}.`;
  return null;
}

function interpolate(source: string, params?: TranslationParams): string {
  if (!params) return source;
  return source.replace(/\{\{([\w.-]+)\}\}|\{([\w.-]+)\}/g, (token, doubleBraceKey, singleBraceKey) => {
    const key = (doubleBraceKey ?? singleBraceKey) as string;
    return Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : token;
  });
}
